'use client'
import { LinePlus } from '@qr-front/icons/line-plus'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { chain } from 'radash'
import { useFormContext } from 'react-hook-form'

import { PHONE_MAX_LENGTH } from '@/constants'
import { useSyncFieldWithAtom } from '@/hooks'
import { addPrefix, filterChars, maxLength, removeAtIndex } from '@/utils'

import { RhcField } from '../../../components/RhcField'
import { InputButton } from '../../InputButton'
import { ContactEntryRow } from './ContactEntryRow'
import {
	capCustomLabel,
	createEntry,
	PHONE_TYPE_OPTIONS,
	TContactEntry,
	TContactType,
} from './contactEntryTypes'

export const contactInfoPhoneListAtom = atomWithReset<TContactEntry[]>([
	createEntry(),
])

const PhoneValueField = ({
	index,
	entry,
	path,
	fieldGroup,
	ariaLabel,
}: {
	index: number
	entry: TContactEntry
	path: string
	fieldGroup?: string
	ariaLabel: string
}) => {
	const t = useTranslations()
	const setPhoneList = useSetAtom(contactInfoPhoneListAtom)
	const form = useFormContext()

	const setValue = (value?: string) => {
		if (value !== undefined) {
			setPhoneList((prev) =>
				prev.map((item, i) =>
					i === index ? { ...item, value } : item,
				),
			)
		}
	}

	return (
		<RhcField
			type="tel"
			form={form}
			path={`${path}.${index}.value`}
			value={entry.value}
			setValue={setValue}
			fieldName="phone"
			fieldGroup={fieldGroup}
			aria-label={ariaLabel}
			onChangeTransform={(event) =>
				chain(
					filterChars('[0-9]'),
					addPrefix('+'),
					maxLength(PHONE_MAX_LENGTH),
				)(event)
			}
			placeholder={t(
				'generator__content_form_section__about__contact_section__phone_placeholder',
			)}
			className="grow"
		/>
	)
}

export const PhoneFields = ({
	path,
	fieldGroup,
}: {
	path: string
	fieldGroup?: string
}) => {
	const t = useTranslations()
	const { getValues, setFocus, setValue, trigger } = useFormContext()

	const [phoneList, setPhoneList] = useAtom(contactInfoPhoneListAtom)

	useSyncFieldWithAtom({
		atom: contactInfoPhoneListAtom,
		setFieldValue: (value) =>
			setValue(
				path,
				value.map((entry) => ({ value: entry.value })),
			),
	})

	const handleAddPhone = () => {
		setPhoneList((prev) => [...prev, createEntry()])
		trigger(path).then(() =>
			setFocus(`${path}.${getValues(path)!.length - 1}.value`),
		)
	}

	const handleRemovePhone = (index: number) => {
		setPhoneList((prev) => removeAtIndex(prev, index))
		trigger(path)
	}

	const handleTypeChange = (index: number, type: TContactType) => {
		setPhoneList((prev) =>
			prev.map((entry, i) => {
				if (i !== index) return entry
				const { label: _label, ...rest } = entry
				return type === 'custom'
					? { ...rest, type, label: '' }
					: { ...rest, type }
			}),
		)
	}

	const handleLabelChange = (index: number, label: string) => {
		setPhoneList((prev) =>
			prev.map((entry, i) =>
				i === index
					? { ...entry, label: capCustomLabel(label) }
					: entry,
			),
		)
	}

	const groupLabel = t(
		'generator__content_form_section__about__contact_section__phone',
	)

	return (
		<>
			{phoneList.map((entry, index) => (
				<ContactEntryRow
					key={index}
					entry={entry}
					options={PHONE_TYPE_OPTIONS}
					fieldId={`${path}-${index}-type`}
					groupLabel={groupLabel}
					showGroupLabel={index === 0}
					onTypeChange={(type) => handleTypeChange(index, type)}
					onLabelChange={(label) => handleLabelChange(index, label)}
					action={
						index === 0 ? (
							<InputButton
								icon={<LinePlus />}
								onClick={handleAddPhone}
								ariaLabel={t(
									'generator__content_form_section__about__contact_section__add_entry',
								)}
							/>
						) : (
							<InputButton
								icon={<LineTrashAlt className="size-5" />}
								onClick={() => handleRemovePhone(index)}
								ariaLabel={t(
									'generator__content_form_section__about__contact_section__remove_entry',
								)}
							/>
						)
					}
				>
					<PhoneValueField
						index={index}
						entry={entry}
						path={path}
						fieldGroup={fieldGroup}
						ariaLabel={groupLabel}
					/>
				</ContactEntryRow>
			))}
		</>
	)
}
